/* C.5 — Company Repository: Single-company create modal.
 *
 * Frozen scope (operator authorisation, 2026-02):
 *   - Required: company_name, isin, asset_type, tier.
 *   - Optional: nse_symbol, bse_code, sector, corporate_domain.
 *   - Calls POST /api/data/admin/companies (data:import gate).
 *   - On 201 success: closes modal + redirects to /data/companies/:identifier
 *     where the identifier is the new nse_symbol, falling back to the
 *     server-issued CMP- id when no symbol is supplied.
 *   - On 409 (duplicate ISIN): shows the server-side conflict message.
 *   - Existing logo workflow / CSV import are NOT touched by this UI.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

// Mirror backend constants so the dropdowns stay tightly aligned.
const ASSET_TYPES = [
    "LISTED_EQUITY", "AMC", "INSURANCE", "BOND_ISSUER",
    "PMS", "AIF", "REIT", "INVIT",
];
const TIERS = ["TIER_1", "TIER_2", "TIER_3"];

// Client-side mirror of the backend's ISIN_RE so we can flag obvious
// bad inputs before the round-trip. Server is still the source of truth.
const ISIN_RE = /^[A-Z]{2}[A-Z0-9]{9}\d$/;

const EMPTY_FORM = {
    company_name: "",
    isin: "",
    asset_type: "LISTED_EQUITY",
    tier: "TIER_2",
    nse_symbol: "",
    bse_code: "",
    sector: "",
    corporate_domain: "",
};

export default function CompanyCreateModal({ open, onClose, onCreated }) {
    // Parent controls mount via `open` (see render guard below). When
    // the modal mounts, ``useState(EMPTY_FORM)`` initialises fresh —
    // no in-effect reset needed, which keeps this component free of
    // the react-hooks/set-state-in-effect pitfall.
    const [form, setForm] = useState(EMPTY_FORM);
    const [busy, setBusy] = useState(false);
    const firstInputRef = useRef(null);
    const navigate = useNavigate();

    // Focus the first input on mount + lock background scroll while
    // the modal is alive. No setState in this effect — it only touches
    // refs and the document body.
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => firstInputRef.current?.focus(), 0);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            clearTimeout(t);
            document.body.style.overflow = prevOverflow;
        };
    }, [open]);

    if (!open) return null;

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const isinTrimmedUpper = (form.isin || "").trim().toUpperCase();
    const isinClientValid = !isinTrimmedUpper || ISIN_RE.test(isinTrimmedUpper);

    const canSubmit =
        !busy &&
        form.company_name.trim().length > 0 &&
        isinTrimmedUpper.length === 12 &&
        ISIN_RE.test(isinTrimmedUpper) &&
        ASSET_TYPES.includes(form.asset_type) &&
        TIERS.includes(form.tier);

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        try {
            const payload = {
                company_name: form.company_name.trim(),
                isin: isinTrimmedUpper,
                asset_type: form.asset_type,
                tier: form.tier,
            };
            // Only send optional fields when the operator entered them
            // so the backend's `_strip_or_none` semantics line up cleanly.
            ["nse_symbol", "bse_code", "sector", "corporate_domain"].forEach((k) => {
                const v = (form[k] || "").trim();
                if (v) payload[k] = v;
            });
            const { data } = await api.post("/data/admin/companies", payload);
            toast.success(`Company created: ${data.company_name} (${data.isin})`);
            onCreated && onCreated(data);
            onClose && onClose();
            const ident = data.nse_symbol || data.id;
            navigate(`/data/companies/${encodeURIComponent(ident)}`);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to create company"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="company-create-modal-backdrop"
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center px-4"
            onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose && onClose(); }}
        >
            <div
                data-testid="company-create-modal"
                className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Data</div>
                        <h2 className="text-base font-semibold text-slate-900">Add Company</h2>
                    </div>
                    <button
                        type="button"
                        data-testid="company-create-modal-close"
                        onClick={() => !busy && onClose && onClose()}
                        className="text-slate-400 hover:text-slate-900 transition-colors"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={submit} className="p-5 space-y-4">
                    {/* Required */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-slate-600">Company Name <span className="text-rose-500">*</span></label>
                        <Input
                            ref={firstInputRef}
                            data-testid="company-create-name"
                            value={form.company_name}
                            onChange={set("company_name")}
                            placeholder="e.g. Tata Consultancy Services Limited"
                            maxLength={200}
                            disabled={busy}
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-slate-600">ISIN <span className="text-rose-500">*</span></label>
                        <Input
                            data-testid="company-create-isin"
                            value={form.isin}
                            onChange={set("isin")}
                            placeholder="e.g. INE467B01029"
                            maxLength={12}
                            className={`font-mono ${!isinClientValid ? "border-rose-300 focus:border-rose-400" : ""}`}
                            disabled={busy}
                        />
                        {!isinClientValid && (
                            <div data-testid="company-create-isin-error" className="text-[11px] text-rose-600">
                                ISIN must be 12 chars: 2 alphabetic + 9 alphanumeric + 1 digit.
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-slate-600">Asset Type <span className="text-rose-500">*</span></label>
                            <select
                                data-testid="company-create-asset-type"
                                value={form.asset_type}
                                onChange={set("asset_type")}
                                disabled={busy}
                                className="w-full h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700"
                            >
                                {ASSET_TYPES.map((at) => (
                                    <option key={at} value={at}>{at.replace(/_/g, " ")}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-slate-600">Tier <span className="text-rose-500">*</span></label>
                            <select
                                data-testid="company-create-tier"
                                value={form.tier}
                                onChange={set("tier")}
                                disabled={busy}
                                className="w-full h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700"
                            >
                                {TIERS.map((t) => (
                                    <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Optional */}
                    <div className="pt-2 border-t border-slate-100">
                        <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-3">Optional</div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-slate-600">NSE Symbol</label>
                                <Input
                                    data-testid="company-create-nse-symbol"
                                    value={form.nse_symbol}
                                    onChange={set("nse_symbol")}
                                    placeholder="TCS"
                                    maxLength={32}
                                    className="font-mono"
                                    disabled={busy}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-slate-600">BSE Code</label>
                                <Input
                                    data-testid="company-create-bse-code"
                                    value={form.bse_code}
                                    onChange={set("bse_code")}
                                    placeholder="532540"
                                    maxLength={12}
                                    className="font-mono"
                                    disabled={busy}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-slate-600">Sector</label>
                                <Input
                                    data-testid="company-create-sector"
                                    value={form.sector}
                                    onChange={set("sector")}
                                    placeholder="IT Services"
                                    maxLength={120}
                                    disabled={busy}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-slate-600">Corporate Domain</label>
                                <Input
                                    data-testid="company-create-domain"
                                    value={form.corporate_domain}
                                    onChange={set("corporate_domain")}
                                    placeholder="tcs.com"
                                    maxLength={120}
                                    disabled={busy}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-3">
                        <Button
                            type="button"
                            variant="outline"
                            data-testid="company-create-cancel"
                            disabled={busy}
                            onClick={() => onClose && onClose()}
                        >Cancel</Button>
                        <Button
                            type="submit"
                            data-testid="company-create-submit"
                            disabled={!canSubmit}
                        >{busy ? "Saving…" : "Create Company"}</Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
